import { useCallback, useRef, PropsWithChildren, useEffect, useReducer } from "react";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { MDSComboboxListItem } from "./combobox-list-item";
import { CommonEditorInput } from "./common-editor-input";
import {
  MDSComboboxColor,
  MDSComboboxItem,
  MDSComboboxSlotName,
  ComboboxState,
  MDSComboboxMention,
} from "./combobox.types";
import { comboboxReducer } from "./combobox.reducer";
import {
  StyledCombobox,
  StyledLabel,
  StyledInputContainer,
  StyledLeftContainer,
  StyledRightContainer,
  StyledLeftIconContainer,
  StyledInputArea,
  StyledErrorMessage,
  StyledActionButtonContainer,
  StyledExpand,
  StyledComboboxPopover,
  StyledPopoverContent,
  StyledEmptyResult,
  StyledMDSListBox,
} from "./combobox.style";
import {
  MemCommonMentionKind,
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
} from "@mem-labs/common-editor";
import type { PopoverProps, Selection } from "react-aria-components";
import { getSlot } from "@/mds/utils/slots";
import { MDSButton } from "../../buttons/button";

export type MDSComboboxFetchDataHandler<T extends MDSComboboxItem> = ({
  query,
}: {
  query: string;
}) => Promise<MDSComboboxDataSource<T>>;

export type MDSComboboxSubmitHandler = (payload: {
  mdxContent: string;
  htmlContent: string;
  mentions: MDSComboboxMention[];
}) => void;

interface Props<T extends MDSComboboxItem> extends PropsWithChildren {
  leftIcon: MdsIconKind;
  label?: string;
  placeholder?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  color?: MDSComboboxColor;
  popoverProps?: PopoverProps;
  loadingDelayMs?: number;
  fetchData: MDSComboboxFetchDataHandler<T>;
  onSubmit?: MDSComboboxSubmitHandler;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputChange?: () => void;
}

export type MDSComboboxDataSource<T extends MDSComboboxItem> = {
  data: T[];
  error?: string;
  isLoading?: boolean;
};

export const MDSCombobox = <T extends MDSComboboxItem>(props: Props<T>) => {
  const {
    fetchData,
    leftIcon,
    label,
    placeholder,
    errorMessage,
    isDisabled = false,
    color,
    children,
    popoverProps,
    loadingDelayMs = 300,
    onSubmit,
    onInputFocus,
    onInputBlur,
    onInputChange,
  } = props;

  const [state, dispatch] = useReducer(comboboxReducer<T>, {
    isOpen: false,
    query: "",
    isEmpty: true,
    isLoading: false,
    data: [],
    hasSearched: false,
  } as ComboboxState<T>);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<MemCommonRichTextInputInstance>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const isProgrammaticFocusRef = useRef<boolean>(false);

  // Track ongoing requests to handle race conditions
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const cleanupLoadingTimeout = () => {
    if (!loadingTimeoutRef.current) {
      return;
    }

    clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = null;
  };

  // Handle mention queries
  const handleMentionQuery = useCallback(
    async (queryStr: string) => {
      const trimmedQuery = queryStr.trim();

      // Check if query starts with mention symbols
      const startsWithMentionSymbol = ["@", "#"].includes(trimmedQuery.charAt(0));

      if (!startsWithMentionSymbol) {
        // Close popover and clear data if no mention symbol
        dispatch({ type: "RESET" });

        // Clear any pending loading timeout
        cleanupLoadingTimeout();

        // Cancel any ongoing request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        return;
      }

      const query = trimmedQuery.slice(1); // Remove the @ or # symbol

      // We want to load even if the query is empty
      if (trimmedQuery.length >= 0) {
        // Cancel any previous request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Clear any pending loading timeout
        cleanupLoadingTimeout();

        // Start new search - this resets state and closes popover
        dispatch({ type: "SEARCH_STARTED" });

        // Create new abort controller for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Set up delayed loading state
        loadingTimeoutRef.current = setTimeout(() => {
          if (!abortController.signal.aborted) {
            dispatch({ type: "LOADING_STARTED" });
          }
        }, loadingDelayMs);

        try {
          const result = await fetchData({ query });

          // Only update if this request wasn't cancelled
          if (!abortController.signal.aborted) {
            dispatch({ type: "SEARCH_COMPLETED", payload: result.data });
            cleanupLoadingTimeout();
          }
        } catch (error) {
          if (abortController.signal.aborted) {
            return;
          }

          console.error("Failed to fetch data:", error);
          dispatch({ type: "SEARCH_FAILED" });
          cleanupLoadingTimeout();
        } finally {
          dispatch({ type: "LOADING_STOPPED" });
        }
      }

      dispatch({ type: "QUERY_CHANGED", payload: query });
    },

    [fetchData, loadingDelayMs]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Handle editor empty state changes
  const handleIsEmptyChange = useCallback((empty: boolean) => {
    dispatch({ type: "EMPTY_STATE_CHANGED", payload: empty });
  }, []);

  const handleSelection = (keys: Selection) => {
    const selected = keys as Set<string>;
    const uuid = selected.values().next().value;
    const item = state.data.find(item => item.id === uuid);

    if (!item) return;

    inputRef.current?.dispatchAction?.({
      kind: MemCommonRichTextInputActionKind.CloseMention,
      payload: {
        id: item?.id,
        kind: item.kind ?? MemCommonMentionKind.Note,
        label: item.label,
        // action,
      },
    });

    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.FocusEditor,
      payload: null,
    });

    dispatch({ type: "POPOVER_CLOSED" });
    dispatch({ type: "QUERY_CHANGED", payload: "" });
  };

  const sendSubmitAction = useCallback(() => {
    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.SendContents,
      payload: null,
    });
  }, []);

  const handleArrowKeyPressed = useCallback(
    (_direction: "up" | "down") => {
      if (!state.isOpen || state.data.length === 0) return;
      isProgrammaticFocusRef.current = true;

      // Focus the listbox and let React Aria handle the navigation
      listboxRef.current?.focus();

      // Clear the flag after a short delay
      setTimeout(() => {
        isProgrammaticFocusRef.current = false;
      }, 100);
    },
    [state.isOpen, state.data]
  );

  const handleEnterPressed = useCallback(() => {
    // Always submit when Enter is pressed
    sendSubmitAction();
  }, [sendSubmitAction]);

  const handleSubmit = useCallback(
    (payload: { mdxContent: string; htmlContent: string; markdownContent: string }) => {
      const { mdxContent, htmlContent } = payload;

      // Parse mentions from the markdownContent
      const mentions = parseMentionsFromMarkdown(mdxContent);

      // Only submit if there's content
      if (mdxContent.trim()) {
        onSubmit?.({
          htmlContent,
          mdxContent,
          mentions,
        });

        // Clear the input after successful submit
        inputRef.current?.dispatchAction?.({
          kind: MemCommonRichTextInputActionKind.SetEditorContent,
          payload: {
            content: "",
          },
        });
      }
    },
    [onSubmit]
  );

  const isActionActive = !isDisabled && !state.isEmpty;

  // Determine which icon to show in the left container
  const displayIcon = state.isLoading ? MdsIconKind.Spinner : leftIcon;

  return (
    <StyledCombobox ref={rootRef} data-mds-name="combobox">
      {label && <StyledLabel>{label}</StyledLabel>}

      <StyledInputContainer ref={inputContainerRef} color={color} aria-disabled={isDisabled}>
        {/* Left addon - icon or custom slot */}
        <StyledLeftContainer color={color}>
          <StyledLeftIconContainer>
            <MdsIcon kind={displayIcon} />
          </StyledLeftIconContainer>
          {getSlot(children, MDSComboboxSlotName.LeftAddon)}
        </StyledLeftContainer>

        {/* Main input area */}
        <StyledInputArea>
          <StyledExpand>
            <CommonEditorInput
              onMentionQuerySet={handleMentionQuery}
              onIsEmptyChange={handleIsEmptyChange}
              placeholder={placeholder}
              isDisabled={isDisabled}
              inputRef={inputRef}
              onSubmit={handleSubmit}
              onArrowKeyPressed={handleArrowKeyPressed}
              onEnterPressed={handleEnterPressed}
              onInputFocus={onInputFocus}
              onInputBlur={onInputBlur}
              onInputChange={onInputChange}
            />
          </StyledExpand>
        </StyledInputArea>

        {/* Right addon - action button or custom slot */}
        <StyledRightContainer color={color}>
          <StyledActionButtonContainer isActive={isActionActive}>
            <MDSButton
              size="sm"
              color="transparent"
              isDisabled={isDisabled}
              onPress={sendSubmitAction}
            >
              <MdsIcon kind={MdsIconKind.SendAlt} />
            </MDSButton>
          </StyledActionButtonContainer>

          {/* Custom right addon slot */}
          {getSlot(children, MDSComboboxSlotName.RightAddon)}
        </StyledRightContainer>
      </StyledInputContainer>

      {errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}

      <StyledComboboxPopover
        isNonModal
        triggerRef={inputContainerRef}
        isOpen={state.isOpen}
        onOpenChange={isOpen => {
          // Don't close popover if we're programmatically focusing the listbox
          if (!isOpen && isProgrammaticFocusRef.current) {
            return;
          }

          // When popover closes, focus the input
          if (!isOpen) {
            inputRef.current?.dispatchAction({
              kind: MemCommonRichTextInputActionKind.FocusEditor,
              payload: null,
            });
          }

          dispatch({ type: isOpen ? "POPOVER_OPENED" : "POPOVER_CLOSED" });
        }}
        placement="bottom start"
        data-mds-name="combobox-popover"
        {...popoverProps}
      >
        <StyledPopoverContent>
          {state.data.length > 0 ? (
            <StyledMDSListBox
              ref={listboxRef}
              onSelectionChange={handleSelection}
              selectionMode="single"
              shouldFocusWrap={true}
              data-mds-name="combobox-list-box"
            >
              {state.data.map((item: MDSComboboxItem) => (
                <MDSComboboxListItem key={item.id} item={item} />
              ))}
            </StyledMDSListBox>
          ) : state.hasSearched ? (
            <StyledEmptyResult>Nothing found</StyledEmptyResult>
          ) : null}
        </StyledPopoverContent>
      </StyledComboboxPopover>
    </StyledCombobox>
  );
};

// Function to parse mentions from markdownContent
const parseMentionsFromMarkdown = (markdownContent: string): MDSComboboxMention[] => {
  const mentions: MDSComboboxMention[] = [];
  const mentionRegex = /<a[^>]*data-type="mention-note"[^>]*>/g;

  let match;
  while ((match = mentionRegex.exec(markdownContent)) !== null) {
    const anchorTag = match[0];

    // Extract data attributes
    const idMatch = anchorTag.match(/data-mention-id="([^"]+)"/);
    const kindMatch = anchorTag.match(/data-mention-kind="([^"]+)"/);
    const labelMatch = anchorTag.match(/data-mention-fallback-label="([^"]+)"/);

    if (idMatch && kindMatch && labelMatch) {
      const id = idMatch[1];
      const kind = kindMatch[1];
      const label = labelMatch[1];

      // Convert string kind to MemCommonMentionKind enum value
      if (kind === "COLLECTION") {
        mentions.push({
          kind: MemCommonMentionKind.Collection,
          collection_id: id,
          collection_title: label,
        });
      } else if (kind === "NOTE") {
        mentions.push({
          kind: MemCommonMentionKind.Note,
          note_id: id,
          note_primary_label: label,
        });
      }
    }
  }

  return mentions;
};
